/**
 * @file statementReviewFlags.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-19
 * @modified 2026-10-19
 *
 * The words for the two statement review flags for Accounts (Cam, 19 Oct
 * 2026), shown in ReviewFlagsPanel. The rules are the server's
 * (utils/statementReviewFlags.js); this only says what they found.
 */

import { formatCurrency } from './currencyFormatter';

export const PAID_AFTER_PERIOD = 'statement_paid_after_period';
export const CREDIT_UNALLOCATED = 'statement_credit_unallocated';

export const STATEMENT_FLAG_LABELS = {
  [PAID_AFTER_PERIOD]: 'Paid just after the period end',
  [CREDIT_UNALLOCATED]: 'Credit not allocated while owing'
};

/** 'YYYY-MM-DD' → 'DD/MM/YYYY', read as that calendar day (never through a Date). */
export function dayText(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '—';
}

/** "a, b and c"; past `max`, "a, b, c and 4 more". */
export function listText(items, max = 6) {
  const list = (items || []).filter(Boolean);
  if (list.length <= 1) return list.join('');
  if (list.length > max) return `${list.slice(0, max).join(', ')} and ${list.length - max} more`;
  return `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`;
}

const money = (v) => formatCurrency(Number(v) || 0);
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/** The customer as the flag names them: "Name (acc)". */
export function customerText(flag) {
  const name = flag?.company_name || 'Unknown customer';
  return flag?.account_no ? `${name} (${flag.account_no})` : name;
}

/**
 * "Paid just after the period end": what it found, and what Accounts should
 * know about it.
 * @returns {{ customer: string, detail: string, hint: string }}
 */
export function paidAfterPeriodText(flag) {
  const invoices = flag?.invoices || [];
  const n = invoices.length;
  const days = (flag?.payment_dates || []).map(dayText);
  const nos = invoices.map(i => i.invoice_no);
  const allPaid = n > 0 && flag.paid_off_count === n;
  const detail = `${plural(n, 'invoice', 'invoices')} owed ${money(flag?.owed_at_end)} at ${dayText(flag?.period_end)}`
    + ` — ${money(flag?.paid_in_window)} paid ${listText(days, 3)}`
    + (allPaid ? ' (now paid in full)' : '')
    + `: ${listText(nos)}`;
  const via = (flag?.bank_accounts || []).length ? ` Paid into ${listText(flag.bank_accounts, 2)}.` : '';
  return {
    customer: customerText(flag),
    detail,
    hint: `The statement will show ${n === 1 ? 'it' : 'them'} as owed. Card (Worldpay) payments settle one to three days after the sale — Accounts can add a note to the statement saying so.${via}`
  };
}

/**
 * "Credit not allocated while owing".
 * @returns {{ customer: string, detail: string, hint: string }}
 */
export function creditUnallocatedText(flag) {
  const credits = flag?.credits || [];
  const docs = credits.map(c => `${c.invoice_no} ${money(c.credit_now)}${c.kind && c.kind !== 'Credit note' ? ` (${c.kind.toLowerCase()})` : ''}`);
  const detail = `${money(flag?.credit_total)} credit unallocated — ${listText(docs, 4)}`
    + ` — while owing ${plural(Number(flag?.owed_count) || 0, 'invoice', 'invoices')} (${money(flag?.owed_total)}) at ${dayText(flag?.period_end)}`;
  return {
    customer: customerText(flag),
    detail,
    hint: 'Accounts can allocate it in Xero before the statement goes, so the customer is not asked for money they have already paid.'
  };
}
